import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { myInvitationsApi, tasksBadge } from "../api";
import CompanyRequisitesModal from "./CompanyRequisitesModal";
import FeedbackModal from "./FeedbackModal";
import LanguageSwitcher from "./LanguageSwitcher";
import { ConsentBanner } from "./ConsentBanner";

const NAV_ITEMS_DEFS = [
  { to: "/dashboard", labelKey: "nav.dashboard", icon: "bi-speedometer2", roles: ["director","hr","manager","employee"] },
  { to: "/timesheet", labelKey: "nav.timesheet", icon: "bi-calendar-check", roles: ["director","hr","manager","employee"] },
  { to: "/tasks", labelKey: "nav.tasks", icon: "bi-check2-square", roles: ["director","hr","manager","employee"] },
  { to: "/documents", labelKey: "nav.documents", icon: "bi-folder2-open", roles: ["director","hr","manager","employee"] },
  { to: "/employees", labelKey: "nav.employees", icon: "bi-people", roles: ["director","hr"] },
  { to: "/payroll", labelKey: "nav.payroll", icon: "bi-cash-coin", roles: ["director","hr","employee","manager"] },
  { to: "/bonuses", labelKey: "nav.bonuses", icon: "bi-gift", roles: ["director","hr","manager","employee"] },
  { to: "/withholdings", labelKey: "nav.withholdings", icon: "bi-dash-circle", roles: ["director","hr","manager","employee"] },
  { to: "/settings", labelKey: "nav.company_settings", icon: "bi-gear", roles: ["director","hr"] },
  { to: "/directory", labelKey: "nav.directory", icon: "bi-journal-text", roles: ["director","hr","manager","employee"] },
  { to: "/profile", labelKey: "nav.profile", icon: "bi-person-circle", roles: ["director","hr","manager","employee"] },
];

const ADMIN_ITEMS = [
  { to: "/admin", labelKey: "admin.nav.dashboard", icon: "bi-speedometer2", end: true },
  { to: "/admin/analytics", labelKey: "admin.nav.analytics", icon: "bi-graph-up" },
  { to: "/admin/companies", labelKey: "admin.nav.companies", icon: "bi-building" },
  { to: "/admin/users", labelKey: "admin.nav.users", icon: "bi-people" },
  { to: "/admin/feedback", labelKey: "admin.nav.feedback", icon: "bi-headset" },
];

export default function Layout({ children }) {
  const { t } = useTranslation();
  const { user, company, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(() => location.pathname.startsWith("/admin"));
  const [pendingCount, setPendingCount] = useState(0);
  const [tasksCount, setTasksCount] = useState(0);
  const [tasksOverdue, setTasksOverdue] = useState(0);
  const [showRequisites, setShowRequisites] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  const role = user?.role || "employee";
  const visible = NAV_ITEMS_DEFS.filter(i => i.roles.includes(role));
  const isSiteAdmin = !!user?.is_admin;
  const invitationsHref = (role === "director" || role === "hr")
    ? "/employees?tab=invitations"
    : "/invitations";

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) setAdminOpen(true);
  }, [location.pathname]);

  // Poll incoming invitations every 30s
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const fetchPending = async () => {
      try {
        const list = await myInvitationsApi();
        if (alive) setPendingCount(list.filter(i => i.status === "pending").length);
      } catch { /* ignore */ }
      try {
        const b = await tasksBadge();
        if (alive) { setTasksCount(b.open || 0); setTasksOverdue(b.overdue || 0); }
      } catch { /* ignore */ }
    };
    fetchPending();
    const t = setInterval(fetchPending, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const avatar = user?.photo_b64 || user?.picture;

  return (
    <div className="d-flex flex-column app-viewport" data-testid="app-layout">
      <nav className="navbar navbar-expand-lg shadow-sm sticky-top" style={{ background: "#0b3d91", color: "white" }}>
        <div className="container-fluid">
          <button
            className="btn btn-link text-white d-lg-none p-0 me-2"
            onClick={() => setOpen(!open)}
            data-testid="sidebar-toggle"
            aria-label={t("common.settings")}
          >
            <i className="bi bi-list fs-3"></i>
          </button>
          <Link to="/dashboard" className="navbar-brand text-white fw-semibold d-flex align-items-center gap-2">
            <i className="bi bi-clock-history"></i>
            <span>Tabel.kz</span>
          </Link>
          <div className="ms-auto d-flex align-items-center gap-3">
            <span className="d-none d-md-inline text-white-50 small" data-testid="navbar-company">
              {company?.name || ""}
            </span>
            <button className="btn btn-sm btn-outline-light"
                    onClick={() => setShowRequisites(true)}
                    data-testid="navbar-requisites-btn"
                    title={t("common.requisites")}>
              <i className="bi bi-card-text me-1"></i>
              <span className="d-none d-md-inline">{t("common.requisites")}</span>
            </button>
            <button className="btn btn-sm btn-outline-light"
                    onClick={() => setShowFeedback(true)}
                    data-testid="navbar-feedback-btn"
                    title={t("auth.contact_admin")}>
              <i className="bi bi-headset me-1"></i>
              <span className="d-none d-md-inline">{t("common.support")}</span>
            </button>
            <LanguageSwitcher />
            <Link to={invitationsHref} className="btn btn-link text-white p-0 position-relative"
                  title={t("nav.invitations")} data-testid="navbar-invitations-bell">
              <i className="bi bi-bell fs-5"></i>
              {pendingCount > 0 && (
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                      style={{ fontSize: "0.6rem" }} data-testid="invitations-badge">
                  {pendingCount}
                </span>
              )}
            </Link>
            <div className="dropdown">
              <button className="btn btn-sm btn-outline-light dropdown-toggle d-flex align-items-center gap-2"
                      data-bs-toggle="dropdown" data-testid="navbar-user-menu">
                {avatar ? (
                  <img src={avatar} alt="" width="24" height="24" className="rounded-circle" style={{ objectFit: "cover" }} />
                ) : (
                  <i className="bi bi-person-circle"></i>
                )}
                <span className="d-none d-sm-inline">{user?.name || user?.email}</span>
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li className="dropdown-header small">{t(`roles.${role}`)}</li>
                <li><Link className="dropdown-item" to="/profile">{t("common.profile")}</Link></li>
                <li><hr className="dropdown-divider"/></li>
                <li><button className="dropdown-item text-danger" onClick={handleLogout} data-testid="logout-button">{t("common.logout")}</button></li>
              </ul>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex-grow-1 d-flex min-h-0 overflow-hidden">
        <aside className={`bg-white border-end overflow-y-auto ${open ? "d-block" : "d-none"} d-lg-block`}
               style={{ width: 240, flexShrink: 0 }} data-testid="sidebar">
          <ul className="nav flex-column p-3 gap-1">
            {visible.map(i => (
              <li className="nav-item" key={i.to}>
                <NavLink to={i.to} end={i.end} onClick={() => setOpen(false)}
                  className={({isActive}) => `nav-link d-flex align-items-center gap-2 rounded ${isActive ? "active text-white" : "text-dark"}`}
                  style={({isActive}) => ({ background: isActive ? "#0b3d91" : "transparent" })}
                  data-testid={`nav-${i.to.replace("/","")}`}>
                  <i className={`bi ${i.icon}`}></i>
                  <span>{t(i.labelKey)}</span>
                  {i.to === "/tasks" && tasksCount > 0 && (
                    <span className={`badge rounded-pill ms-auto ${tasksOverdue > 0 ? "bg-danger tabel-pulse" : "bg-primary"}`}
                          data-testid="nav-tasks-badge"
                          title={tasksOverdue > 0 ? `${tasksOverdue} ${t("tasks.overdue")}` : t("tasks.kanban_open")}>
                      {tasksCount}
                    </span>
                  )}
                </NavLink>
              </li>
            ))}

            {isSiteAdmin && (
              <li className="nav-item mt-2 pt-2 border-top" data-testid="nav-admin-group">
                <button
                  type="button"
                  className="nav-link d-flex align-items-center gap-2 rounded w-100 text-dark border-0 bg-transparent text-start"
                  onClick={() => setAdminOpen((v) => !v)}
                  aria-expanded={adminOpen}
                  data-testid="nav-admin-toggle"
                >
                  <i className="bi bi-shield-lock"></i>
                  <span className="fw-semibold flex-grow-1">{t("nav.admin")}</span>
                  <i className={`bi ${adminOpen ? "bi-chevron-up" : "bi-chevron-down"} small`}></i>
                </button>
                {adminOpen && (
                  <ul className="nav flex-column gap-1 ms-2 ps-2 border-start mt-1">
                    {ADMIN_ITEMS.map((i) => (
                      <li className="nav-item" key={i.to}>
                        <NavLink
                          to={i.to}
                          end={i.end}
                          onClick={() => setOpen(false)}
                          className={({ isActive }) =>
                            `nav-link d-flex align-items-center gap-2 rounded py-1 ${isActive ? "active text-white" : "text-dark"}`
                          }
                          style={({ isActive }) => ({ background: isActive ? "#0b3d91" : "transparent", fontSize: "0.92rem" })}
                          data-testid={`nav-admin-${i.to.replace(/\//g, "-")}`}
                        >
                          <i className={`bi ${i.icon}`}></i>
                          <span>{t(i.labelKey)}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            )}
          </ul>
        </aside>
        <main
          className="flex-grow-1 p-3 p-md-4 d-flex flex-column min-h-0 overflow-hidden"
          style={{ background: "#f5f7fb", minWidth: 0 }}
        >
          <div className="layout-page">
            <div className="flex-shrink-0"><ConsentBanner /></div>
            <div className="layout-page-body">{children}</div>
          </div>
        </main>
      </div>
      <CompanyRequisitesModal open={showRequisites} onClose={() => setShowRequisites(false)} />
      <FeedbackModal open={showFeedback} onClose={() => setShowFeedback(false)} />
    </div>
  );
}
